import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InfoIcon from '@mui/icons-material/Info';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import BarChartIcon from '@mui/icons-material/BarChart';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SearchIcon from '@mui/icons-material/Search';
import RemoveIcon from '@mui/icons-material/Remove';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { marketPredictionService, PricePrediction } from '../../services/marketPredictionService';
import { counties } from '../../data/counties';
import { crops } from '../../data/crops';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MarketPrediction: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedCrop, setSelectedCrop] = useState<string | null>(null);
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [prediction, setPrediction] = useState<PricePrediction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!selectedCrop) return;

    setLoading(true);
    setError(null);

    try {
      const result = await marketPredictionService.getPrediction(selectedCrop || "", selectedCounty || "");
      setPrediction(result);
      setLoading(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch prediction.');
      setLoading(false);
    }
  };

  const generatePriceChart = () => {
    if (!prediction) return null;

    const data = {
      labels: prediction.predictedPrices.map(point => point.month),
      datasets: [
        {
          label: `${prediction.cropName} Price (KES/kg)`,
          data: prediction.predictedPrices.map(point => point.price),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.3,
          fill: true,
          pointBackgroundColor: 'rgb(75, 192, 192)',
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        tooltip: {
          callbacks: {
            label: function(context: any) {
              return `Price: KES ${context.raw.toLocaleString()}`;
            }
          }
        },
        title: {
          display: true,
          text: `${prediction.cropName} Price Forecast (Next 6 Months)`,
          font: {
            size: 16,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          title: {
            display: true,
            text: 'Price (KES/kg)',
          },
          ticks: {
            callback: function(value: any) {
              return 'KES ' + value.toLocaleString();
            }
          }
        },
      },
    };

    return <Line data={data} options={options} />;
  };

  const getImpactIcon = (impact: 'positive' | 'negative' | 'neutral') => {
    switch (impact) {
      case 'positive':
        return <ArrowUpwardIcon sx={{ color: 'success.main' }} />;
      case 'negative':
        return <ArrowDownwardIcon sx={{ color: 'error.main' }} />;
      case 'neutral':
        return <RemoveIcon sx={{ color: 'text.secondary' }} />;
      default:
        return null;
    }
  };

  const getPriceChangeIndicator = () => {
    if (!prediction) return null;
    
    const currentPrice = prediction.currentPrice;
    const futurePrice = prediction.predictedPrices[prediction.predictedPrices.length - 1].price;
    const priceDifference = futurePrice - currentPrice;
    const percentageChange = (priceDifference / currentPrice) * 100;
    
    let icon;
    let color;
    
    if (percentageChange > 5) {
      icon = <TrendingUpIcon />;
      color = 'success.main';
    } else if (percentageChange < -5) {
      icon = <TrendingDownIcon />;
      color = 'error.main';
    } else {
      icon = <TrendingUpIcon />;
      color = 'warning.main';
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Box sx={{ color, mr: 1, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography variant="body2" sx={{ color }}>
          {priceDifference > 0 ? '+' : ''}{priceDifference.toLocaleString()} KES ({percentageChange.toFixed(1)}%)
          over the next 6 months
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          AI Market Price Predictions
        </Typography>
        <Typography variant="body1" paragraph>
          Get AI-powered forecasts of future crop prices to help you make better planting and selling decisions.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={crops}
              value={selectedCrop}
              onChange={(_, newValue) => setSelectedCrop(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Crop"
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={counties}
              value={selectedCounty}
              onChange={(_, newValue) => setSelectedCounty(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select County (Optional)"
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!selectedCrop || loading}
              sx={{ height: '56px' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Predict'}
            </Button>
          </Grid>
        </Grid>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Analyzing market data...
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Our AI is processing historical prices and market factors
              </Typography>
            </Box>
          </Box>
        ) : prediction ? (
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Paper elevation={2} sx={{ p: 2 }}>
                {generatePriceChart()}
              </Paper>
              
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Confidence Level: {(prediction.confidenceLevel * 100).toFixed(0)}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Last Updated: {new Date(prediction.lastUpdated).toLocaleDateString()}
                  </Typography>
                </Box>
                <Chip 
                  icon={<InfoIcon />} 
                  label="Based on historical data and market trends" 
                  variant="outlined" 
                  size="small" 
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Current & Predicted Prices
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="body1">
                    Current Price:
                    <Box component="span" sx={{ fontWeight: 'bold', ml: 1 }}>
                      KES {prediction.currentPrice.toLocaleString()}/kg
                    </Box>
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Predicted Price (6 months):
                    <Box component="span" sx={{ fontWeight: 'bold', ml: 1 }}>
                      KES {prediction.predictedPrices[prediction.predictedPrices.length - 1].price.toLocaleString()}/kg
                    </Box>
                  </Typography>
                  
                  {getPriceChangeIndicator()}
                </CardContent>
              </Card>
              
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Market Factors
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <List dense>
                    {prediction.factors.map((factor, index) => (
                      <ListItem key={`factor-${index}`}>
                        <ListItemIcon>
                          {getImpactIcon(factor.impact)}
                        </ListItemIcon>
                        <ListItemText 
                          primary={factor.factor}
                          secondary={factor.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <LightbulbIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20, color: 'warning.main' }} />
                    Recommendations
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <List dense>
                    {prediction.recommendations.map((recommendation, index) => (
                      <ListItem key={`recommendation-${index}`}>
                        <ListItemIcon>
                          <InfoIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={recommendation} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f9f9f9', borderRadius: 2 }}>
            <BarChartIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7 }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Select a crop to see price predictions
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}>
              Our AI analyzes historical data, weather patterns, market trends, and other factors to predict
              future prices for your selected crop.
            </Typography>
          </Box>
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          How Our Price Predictions Work
        </Typography>
        
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Data Sources
                </Typography>
                <Typography variant="body2" paragraph>
                  Our AI analyzes data from multiple sources including:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Historical price records" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Weather forecasts" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Government import/export data" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Global market trends" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Local supply and demand patterns" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Prediction Methodology
                </Typography>
                <Typography variant="body2" paragraph>
                  Our predictions are generated using advanced machine learning algorithms that:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Identify seasonal patterns in crop prices" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Detect correlations between different market factors" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Account for regional variations in pricing" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Adjust for inflation and economic trends" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Continuously learn from new data" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Using Predictions Effectively
                </Typography>
                <Typography variant="body2" paragraph>
                  To make the most of our price predictions:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Consider the confidence level of each prediction" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Use predictions as one of several decision-making tools" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Check predictions regularly as they update with new data" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Compare predictions across different crops when planning" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Combine with local knowledge of your specific conditions" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default MarketPrediction;
